/**
 * Human-readable copy for a machine failure token from the server
 * publish-failure classifier.
 *
 * POLICY — actionable-only: we show a SPECIFIC message only when the user can
 * actually DO something about it (reconnect, grant a permission, pick a
 * destination, switch account type, or clear a provider-side restriction).
 * Every other failure — rate limits, quota, media/format problems, transient
 * glitches, provider rejections, our-side bugs, and any unknown/unmapped
 * token — collapses to ONE `genericCopy` general error. The point is to never
 * put technical detail the user can't act on in front of them; the per-platform
 * "Retry" affordance is the action for those cases.
 *
 * Rules:
 *  - NEVER return the raw machine token or the raw platform error.
 *  - Anything not in `COPY_MAP` (the actionable set) → `genericCopy`.
 *  - `ctaLabel` + `href` point at a single unambiguous client route
 *    (`/integrations`). `account_restricted` omits `href` — the PostModal row
 *    renders bespoke external unlock links per platform.
 */
export interface PublishFailureCopy {
  title: string
  body: string
  ctaLabel?: string
  href?: string
}

type CopyBuilder = (platformLabel: string) => PublishFailureCopy

const INTEGRATIONS_HREF = '/integrations'

// ACTIONABLE reasons ONLY. If the user can DO something about it — reconnect,
// grant a permission, pick a destination, switch account type, or clear a
// provider-side restriction — it gets a specific title/body/CTA. Every other
// failure (rate limits, quota, media/format problems, transient glitches,
// provider rejections, our-side bugs, unknown/unmapped tokens) is NOT
// user-fixable, so it falls through to the single `genericCopy` below. The
// per-platform "Retry" button is the action for those; we don't surface
// technical detail the user can't act on.
const COPY_MAP: Record<string, CopyBuilder> = {
  no_token: p => ({
    title: `${p} isn't connected`,
    body: `Connect your ${p} account so Taka can publish this post.`,
    ctaLabel: `Connect ${p}`,
    href: INTEGRATIONS_HREF,
  }),
  token_expired: p => ({
    title: `${p} connection expired`,
    body: `Reconnect your ${p} account and Taka will finish publishing this post.`,
    ctaLabel: `Reconnect ${p}`,
    href: INTEGRATIONS_HREF,
  }),
  missing_permissions: p => ({
    title: `${p} is missing a permission`,
    body: `Reconnect ${p} and grant every requested permission so Taka can publish.`,
    ctaLabel: `Reconnect ${p}`,
    href: INTEGRATIONS_HREF,
  }),
  no_channel: p => ({
    title: `Pick a ${p} destination`,
    body: `Select a Page or channel for ${p} in Integrations so Taka knows where to publish.`,
    ctaLabel: 'Open Integrations',
    href: INTEGRATIONS_HREF,
  }),
  // A TikTok post reached publish without the user's per-post choices
  // (audience, interactions, commercial disclosure). Fixable from the post
  // itself: the composer's TikTok dialog / editor asks for them.
  missing_post_settings: p => ({
    title: `Choose who can view this ${p} post`,
    body: `${p} needs you to pick the audience and post settings for this video before it can go live. Open the post, choose them, and publish again.`,
  }),
  personal_account: p => ({
    title: `${p} needs a business account`,
    body: `Switch this ${p} account to a Business or Creator profile. Personal accounts can't publish through Taka.`,
    ctaLabel: 'Open Integrations',
    href: INTEGRATIONS_HREF,
  }),
  off_facebook_activity: p => ({
    title: `${p} blocked this post`,
    body: `${p} blocked the post under its off-platform activity rules. Reconnect the account or contact ${p} support.`,
    ctaLabel: `Reconnect ${p}`,
    href: INTEGRATIONS_HREF,
  }),
  account_restricted: p => ({
    // No href — the PostModal row renders bespoke external unlock links per
    // platform. Two indicators per row is worse than none. Body wording
    // mirrors the PostModal bespoke branch so the two surfaces read
    // identically.
    title: `${p} restricted this account`,
    body: `Sort it out with ${p}, then reset the connection on the Integrations page.`,
  }),
  // Not user-fixable, and a retry fails the same way, so no "please try again".
  app_unaudited: p => ({
    title: `${p} didn't publish this post`,
    body: `${p} only lets Taka post to private accounts right now, so this post wasn't published. Nothing to fix on your side.`,
  }),
  // Taka's own render path could not produce the post's image before the
  // scheduled publish (Worker outage past the retry horizon, or a document it
  // rejects). The generic "something went wrong publishing to X" would blame
  // the platform; say what actually happened. Retry re-renders the latest
  // edits before publishing, so the Retry affordance is the honest action.
  render_unavailable: p => ({
    title: `Couldn't render this post's image`,
    body: `Taka couldn't render the latest version of this post's image, so it wasn't published to ${p}. Retry to render and publish it again.`,
  }),
}

/**
 * The single general error for anything NOT user-fixable. No technical detail,
 * no misleading "reconnect" advice (reconnect cases are handled above) — just a
 * plain statement + the Retry affordance that lives beside it.
 */
function genericCopy(platformLabel: string): PublishFailureCopy {
  return {
    title: `Couldn't publish to ${platformLabel}`,
    body: `Something went wrong publishing to ${platformLabel}. Please try again.`,
  }
}

export function getPublishFailureCopy(
  reason: string | undefined,
  platformLabel: string
): PublishFailureCopy {
  if (!reason) return genericCopy(platformLabel)
  const build = COPY_MAP[reason]
  return build ? build(platformLabel) : genericCopy(platformLabel)
}
